import { Button, Flex, GlobalError, Img } from '@dnb/eufemia';
import { ErrorContext, useNavHistory } from '@portals/shared-frontend/hooks';
import { ErrorCode } from '@portals/shared-frontend/hooks/ErrorContext';
import { ReactNode, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';
import { useUser } from '@/hooks/useUser';

import BaseLayout from '../BaseLayout';

import WomanSearchingLight from '@/illustrations/WomanSearchingLight.svg?url';

interface Props {
  errorCode: ErrorCode;
}

export function GlobalErrorPage({ errorCode }: Props) {
  const { setGlobalError } = useContext(ErrorContext);
  const { removePage } = useNavHistory();
  const navigate = useNavigate();

  const location = useLocation();
  const initialLocationKeyRef = useRef(location.key);

  const { refresh: refreshAuth, isLoading: isLoadingAuth } = useAuth();
  const { user, isLoading: isLoadingUser } = useUser();
  const initialUserEmailRef = useRef(user?.email);
  const isAuthRefreshed = useRef<boolean>(false);

  // Reset error on navigation, also works when navigating to the same page
  useEffect(() => {
    if (location.key !== initialLocationKeyRef.current) {
      setGlobalError(null);
    }
  }, [location.key, setGlobalError]);

  // Refresh auth state on statuses associated with unauthenticated access
  useEffect(() => {
    if (isAuthRefreshed.current) return;
    if (errorCode === 401 || errorCode === 403 || errorCode === 404) {
      void refreshAuth();
    }
    isAuthRefreshed.current = true;
  }, [errorCode, refreshAuth]);

  // Remove page from pinned & recent navigation history if error is 403 or 404
  useEffect(() => {
    if (!isAuthRefreshed.current || isLoadingAuth || isLoadingUser) return; // Wait for auth refresh to complete
    if (initialUserEmailRef.current !== user?.email) return; // Don't remove page if user changed during auth refresh
    if (location.pathname !== '/' && errorCode && (errorCode === 403 || errorCode === 404)) {
      removePage(location.pathname);
    }
  }, [errorCode, location.pathname, removePage, isLoadingAuth, user?.email, isLoadingUser]);

  const hasPreviousPage = globalThis.history.state && globalThis.history.state.idx > 0;

  return (
    <BaseLayout>
      <GlobalError {...getErrorInfoFromStatusCode(errorCode)} bottom="large" top="large">
        <Img alt="A woman searching in a box" src={WomanSearchingLight} width={320} />
        <Flex.Horizontal gap="large" justify="center">
          <Button element={Link} size="large" to="/" top="large">
            Go to landing page
          </Button>
          {hasPreviousPage && (
            <Button onClick={() => navigate(-1)} size="large" top="large">
              Go back to previous page
            </Button>
          )}
        </Flex.Horizontal>
      </GlobalError>
    </BaseLayout>
  );
}

interface ErrorInfo {
  title: string;
  statusCode: string;
  errorMessageCode?: ReactNode;
  text: string;
}

function getErrorInfoFromStatusCode(errorCode: ErrorCode): ErrorInfo {
  switch (errorCode) {
    case 400: {
      return {
        title: 'Bad Request',
        statusCode: '400',
        text: 'Something was wrong with the information sent to the server. Please check your input and try again.',
      };
    }
    case 401: {
      return {
        title: 'Unauthorized',
        statusCode: '401',
        text: 'You need to be logged in to access this content. Please sign in and try again.',
      };
    }
    case 403: {
      return {
        title: 'You do not have permission to view this page',
        statusCode: '403',
        text: 'Are you sure you have gone to the right place? You might not have the necessary access rights.',
      };
    }
    case 404: {
      return {
        title: `We can't find the page you're looking for …`,
        statusCode: '404',
        text: 'Are you sure you have entered the correct address? Or have we messed with the links?',
      };
    }
    case 502: {
      return {
        title: 'Bad Gateway',
        statusCode: '502',
        text: 'The server is experiencing issues right now. Please try again in a moment.',
      };
    }
    case 503: {
      return {
        title: 'Service Unavailable',
        statusCode: '503',
        text: 'The service is temporarily unavailable. Please try again later.',
      };
    }
    default: {
      const response: ErrorInfo = {
        title: 'Something Went Wrong',
        statusCode: String(errorCode),
        text: 'An unexpected error occurred. Please try again later.',
      };
      if (errorCode === 'unknown') {
        response.errorMessageCode = null; // Hides status code display for unknown codes
      }
      return response;
    }
  }
}
